

























































































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { HelpService } from '@/services/help-service';
import { ColorService } from '@/services/color-service';
import { cost, duration, factor, formatNodeProp, keysToString, truncate, rows } from '@/filters';
import { EstimateDirection, HighlightType, NodeProp, nodePropTypes, Orientation,
         PropType, ViewMode, WorkerProp } from '@/enums';
import * as _ from 'lodash';

@Component({
  name: 'plan-node',
  filters: {
    cost,
    duration,
    factor,
    formatNodeProp,
    keysToString,
    truncate,
    rows,
  },
})
export default class PlanNode extends Vue {
  @Prop(Object) public node!: any;
  public executionTimePercent: number = NaN;
  @Prop(Object) private plan!: any;
  @Prop(Object) private viewOptions!: any;
  @Prop() private eventBus!: InstanceType<typeof Vue>;

  // UI flags
  private showDetails: boolean = false;
  private collapsed: boolean = false;
  private activeTab: string = 'general';

  // calculated properties
  private costPercent: number = NaN;
  private barWidth: number = 0;
  private highlightValue?: string | null;
  private props: any[] = [];
  private plans: any[] = [];
  private plannerRowEstimateValue?: number;
  private plannerRowEstimateDirection?: EstimateDirection;
  private rowsRemoved: number = NaN;
  private rowsRemovedPercent: number = NaN;

  // required for custom change detection
  private currentCompactView?: boolean;

  // expose enum to view
  private estimateDirections = EstimateDirection;
  private highlightTypes = HighlightType;
  private viewModes = ViewMode;
  private orientations = Orientation;

  private nodeProps = NodeProp;
  private workerProps = WorkerProp;
  private helpService = new HelpService();
  private colorService = new ColorService();
  private lodash = _;

  // Returns the list of properties that have already been displayed either in
  // the main panel or in other detailed tabs.
  private notMiscProperties: string[] = [
      NodeProp.NODE_TYPE,
      NodeProp.CTE_NAME,
      NodeProp.EXCLUSIVE_DURATION,
      NodeProp.EXCLUSIVE_COST,
      NodeProp.TOTAL_COST,
      NodeProp.PLAN_ROWS,
      NodeProp.ACTUAL_ROWS,
      NodeProp.ACTUAL_LOOPS,
      NodeProp.OUTPUT,
      NodeProp.WORKERS,
      NodeProp.WORKERS_PLANNED,
      NodeProp.WORKERS_LAUNCHED,
      NodeProp.EXCLUSIVE_SHARED_HIT_BLOCKS,
      NodeProp.EXCLUSIVE_SHARED_READ_BLOCKS,
      NodeProp.EXCLUSIVE_SHARED_DIRTIED_BLOCKS,
      NodeProp.EXCLUSIVE_SHARED_WRITTEN_BLOCKS,
      NodeProp.EXCLUSIVE_TEMP_READ_BLOCKS,
      NodeProp.EXCLUSIVE_TEMP_WRITTEN_BLOCKS,
      NodeProp.EXCLUSIVE_LOCAL_HIT_BLOCKS,
      NodeProp.EXCLUSIVE_LOCAL_READ_BLOCKS,
      NodeProp.EXCLUSIVE_LOCAL_DIRTIED_BLOCKS,
      NodeProp.EXCLUSIVE_LOCAL_WRITTEN_BLOCKS,
      NodeProp.SHARED_HIT_BLOCKS,
      NodeProp.SHARED_READ_BLOCKS,
      NodeProp.SHARED_DIRTIED_BLOCKS,
      NodeProp.SHARED_WRITTEN_BLOCKS,
      NodeProp.TEMP_READ_BLOCKS,
      NodeProp.TEMP_WRITTEN_BLOCKS,
      NodeProp.LOCAL_HIT_BLOCKS,
      NodeProp.LOCAL_READ_BLOCKS,
      NodeProp.LOCAL_DIRTIED_BLOCKS,
      NodeProp.LOCAL_WRITTEN_BLOCKS,
      NodeProp.PLANNER_ESTIMATE_FACTOR,
      NodeProp.PLANNER_ESTIMATE_DIRECTION,
      NodeProp.SUBPLAN_NAME,
      NodeProp.GROUP_KEY,
      NodeProp.HASH_CONDITION,
      NodeProp.JOIN_TYPE,
      NodeProp.INDEX_NAME,
      NodeProp.HASH_CONDITION,
      NodeProp.EXCLUSIVE_IO_READ_TIME,
      NodeProp.EXCLUSIVE_IO_WRITE_TIME,
      NodeProp.IO_READ_TIME, // Exclusive value already shown in IO tab
      NodeProp.IO_WRITE_TIME, // Exclusive value already shown in IO tab
      NodeProp.HEAP_FETCHES,
      NodeProp.WAL_RECORDS,
      NodeProp.WAL_BYTES,
      NodeProp.WAL_FPI,
  ];

  private created(): void {
    this.calculateProps();
    this.calculateBar();
    this.calculateDuration();
    this.calculateCost();
    this.calculateRowsRemoved();

    this.plans = this.node[NodeProp.PLANS];

    this.plannerRowEstimateDirection = this.node[NodeProp.PLANNER_ESTIMATE_DIRECTION];
    this.plannerRowEstimateValue = this.node[NodeProp.PLANNER_ESTIMATE_FACTOR];
    this.plan.nodeComponents.push(this);
  }

  private destroyed(): void {
    _.remove(this.plan.nodeComponents, (cmp) => cmp === this);
  }

  private calculateDuration() {
    // use the first node total time if plan execution time is not available
    const executionTime = this.plan.planStats.executionTime || this.plan.content.Plan[NodeProp.ACTUAL_TOTAL_TIME];
    this.executionTimePercent = _.round(
      (this.node[NodeProp.EXCLUSIVE_DURATION] / executionTime) * 100);
  }

  private calculateCost() {
    const maxTotalCost = this.plan.content.maxTotalCost;
    this.costPercent = _.round((this.node[NodeProp.EXCLUSIVE_COST] / maxTotalCost) * 100);
  }

  private get rowsRemovedProp() {
    const nodeKey = Object.keys(this.node).find(
      (key) => key === NodeProp.ROWS_REMOVED_BY_FILTER || key === NodeProp.ROWS_REMOVED_BY_JOIN_FILTER,
    );
    type NodePropStrings = keyof typeof NodeProp;
    return Object.keys(NodeProp).find((prop) => NodeProp[prop as NodePropStrings] === nodeKey);
  }

  private calculateRowsRemoved() {
    const rowsRemovedProp = this.rowsRemovedProp;

    if (rowsRemovedProp) {
      type NodePropStrings = keyof typeof NodeProp;
      const removed = this.node[NodeProp[rowsRemovedProp as NodePropStrings]];
      this.rowsRemoved = removed;
      const actual = this.node[NodeProp.ACTUAL_ROWS];
      this.rowsRemovedPercent = _.floor(removed / (removed + actual) * 100);
    }
  }

  // create an array of node propeties so that they can be displayed in the view
  private calculateProps() {
    this.props = _.chain(this.node)
      .omit(NodeProp.PLANS)
      .omit(NodeProp.WORKERS)
      .map((value, key) => {
        return { key, value };
      })
      .value();
  }

  private getNodeTypeDescription() {
    return this.helpService.getNodeTypeDescription(this.node[NodeProp.NODE_TYPE]);
  }

  private getNodeName(): string {
    let nodeName = this.isParallelAware ? 'Parallel ' : '';
    nodeName += this.node[NodeProp.NODE_TYPE];
    if (this.viewOptions.viewMode === ViewMode.DOT && !this.showDetails) {
      return nodeName.replace(/[^A-Z]/g, '').toUpperCase();
    }
    return nodeName.toUpperCase();
  }

  private get shouldShowPlannerEstimate() {
    if ((this.collapsed && !this.showDetails) || this.viewOptions.viewMode === ViewMode.DOT) {
      return false;
    }
    return (this.estimationClass || this.showDetails) &&
      this.plannerRowEstimateDirection !== EstimateDirection.none &&
      this.plannerRowEstimateValue;
  }

  private get shouldShowCost() {
    if ((this.collapsed && !this.showDetails) || this.viewOptions.viewMode === ViewMode.DOT) {
      return false;
    }

    return this.node[NodeProp.EXCLUSIVE_COST] && (this.costClass || this.showDetails);
  }

  private shouldShowNodeBarLabel(): boolean {
    if (this.showDetails) {
      return true;
    }

    if (this.collapsed || this.viewOptions.viewMode === ViewMode.DOT) {
      return false;
    }

    return true;
  }

  private get shouldShowRowsRemoved(): boolean {
    return !!this.rowsRemovedClass && this.viewOptions.viewMode === ViewMode.FULL && !this.collapsed;
  }

  @Watch('viewOptions.highlightType')
  private calculateBar(): void {
    let value: number;
    if (!this.$options || !this.$options.filters) {
      return;
    }
    switch (this.viewOptions.highlightType) {
      case HighlightType.DURATION:
        value = (this.node[NodeProp.EXCLUSIVE_DURATION]);
        if (value === undefined) {
          this.highlightValue = null;
          break;
        }
        this.barWidth = Math.round(value / this.plan.planStats.maxDuration * 100);
        this.highlightValue = this.$options.filters.duration(value);
        break;
      case HighlightType.ROWS:
        value = (this.node[NodeProp.ACTUAL_ROWS]);
        if (value === undefined) {
          this.highlightValue = null;
          break;
        }
        this.barWidth = Math.round(value / this.plan.planStats.maxRows * 100) || 0;
        this.highlightValue = this.$options.filters.rows(value);
        break;
      case HighlightType.COST:
        value = (this.node[NodeProp.EXCLUSIVE_COST]);
        if (value === undefined) {
          this.highlightValue = null;
          break;
        }
        this.barWidth = Math.round(value / this.plan.planStats.maxCost * 100);
        this.highlightValue = this.$options.filters.cost(value);
        break;
    }
  }

  private getBarColor(percent: number) {
    return this.colorService.numberToColorHsl(percent);
  }

  private get durationClass() {
    let c;
    const i = this.executionTimePercent;
    if (i > 90) {
      c = 4;
    } else if (i > 40) {
      c = 3;
    } else if (i > 10) {
      c = 2;
    }
    if (c) {
      return 'c-' + c;
    }
    return false;
  }

  private get estimationClass() {
    let c;
    const i = this.node[NodeProp.PLANNER_ESTIMATE_FACTOR];
    if (i > 1000) {
      c = 4;
    } else if (i > 100) {
      c = 3;
    } else if (i > 10) {
      c = 2;
    }
    if (c) {
      return 'c-' + c;
    }
    return false;
  }

  private get costClass() {
    let c;
    const i = this.costPercent;
    if (i > 90) {
      c = 4;
    } else if (i > 40) {
      c = 3;
    } else if (i > 10) {
      c = 2;
    }
    if (c) {
      return 'c-' + c;
    }
    return false;
  }

  private get rowsRemovedClass() {
    let c;
    // high percent of rows removed is relevant only when duration is high
    // as well
    const i = this.rowsRemovedPercent * this.executionTimePercent;
    if (i > 2000) {
      c = 4;
    } else if (i > 500) {
      c = 3;
    }
    if (c) {
      return 'c-' + c;
    }
    return false;
  }

  private get heapFetchesClass() {
    let c;
    const i = this.node[NodeProp.HEAP_FETCHES] /
      (this.node[NodeProp.ACTUAL_ROWS] +
       (this.node[NodeProp.ROWS_REMOVED_BY_FILTER] || 0) +
       (this.node[NodeProp.ROWS_REMOVED_BY_JOIN_FILTER] || 0)
      ) * 100;
    if (i > 90) {
      c = 4;
    } else if (i > 40) {
      c = 3;
    } else if (i > 10) {
      c = 2;
    }
    if (c) {
      return 'c-' + c;
    }
    return false;
  }

  private toggleCollapsed() {
    this.collapsed = !this.collapsed;
  }

  private get hasChildren(): boolean {
    return !!this.plans;
  }

  private get workersCount(): number {
    if (_.isArray(this.node[NodeProp.WORKERS])) {
      return this.node[NodeProp.WORKERS].length;
    }
    return parseInt(this.node[NodeProp.WORKERS], 0);
  }

  private get workersCountReversed(): number[] {
    return [...Array(this.workersCount).keys()].slice().reverse();
  }

  private get isParallelAware(): boolean {
    return this.node[NodeProp.PARALLEL_AWARE];
  }

  private get allWorkersLaunched(): boolean {
    return !this.node[NodeProp.WORKERS_LAUNCHED] ||
      this.node[NodeProp.WORKERS_PLANNED] === this.node[NodeProp.WORKERS_LAUNCHED];
  }

  private getHelpMessage(message: string) {
    return this.helpService.getHelpMessage(message);
  }

  private shouldShowProp(key: string, value: any): boolean {

    return (value ||
            nodePropTypes[key] === PropType.increment ||
            key === NodeProp.ACTUAL_ROWS) &&
           this.notMiscProperties.indexOf(key) === -1;
  }

  private get isNeverExecuted(): boolean {
    return this.plan.planStats.executionTime && !this.node[NodeProp.ACTUAL_LOOPS];
  }

  private get hasSeveralLoops(): boolean {
    return this.node[NodeProp.ACTUAL_LOOPS] > 1;
  }

  private get tilde(): string {
    return this.hasSeveralLoops ? '~' : '';
  }

  private setActiveTab(tab: string) {
    this.activeTab = tab;
  }

  // returns the formatted prop
  private formattedProp(propName: keyof typeof NodeProp) {
    const property = NodeProp[propName];
    const value = this.node[property];
    if (!value) {
      return '';
    }
    return this.$options!.filters!.formatNodeProp(property, value, true);
  }
}
